import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Calendar, ArrowLeft, FileText, RotateCcw, Upload } from "lucide-react";
import { toast } from "react-hot-toast";
import Sidebar from "../../components/Sidebar";
import DisableUserModal from "../../components/ui/DisableUserModal";
import DeclineKYCModal from "../../components/ui/DeclinKycModal";
import ResetPinModal from "../../components/ui/ResetPinModal";
import {
  useUserById,
  useDisableUserById,
  useEnableUserById,
  useDeleteUserById,
  useRestoreUserById,
  useRetryKyc,
  useUploadKycDocument,
  useUpdateUser,
  useApproveKycById,
  useUpdateKycById,
  useRequestResetUserPin,
  useConfirmResetUserPin,
} from "../../services/queries/queries";

const KycDocumentPreview = ({ label, url, format, uuid, fieldKey, onUploaded }) => {
  const uploadKycDocument = useUploadKycDocument();
  const isPdf = format === "pdf";

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await uploadKycDocument.mutateAsync({ uuid, file });
      onUploaded(fieldKey, result.data);
      toast.success(`${label} replaced successfully`);
    } catch (err) {
      toast.error(err?.response?.data?.message || `Failed to upload ${label}`);
    }
    e.target.value = "";
  };

  return (
    <div>
      <label className="block mb-1 text-sm text-gray-600">{label}</label>
      <div className="p-3 rounded-2xl border border-gray-200">
        {url ? (
          isPdf ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex gap-2 items-center text-sm font-medium text-emerald-700 hover:underline"
            >
              <FileText size={16} /> View PDF document
            </a>
          ) : (
            <img src={url} alt={label} className="object-contain w-full max-h-48 rounded-lg" />
          )
        ) : (
          <span className="text-sm text-gray-400">Not provided</span>
        )}

        <label className="flex gap-2 items-center mt-3 text-xs font-medium text-emerald-600 cursor-pointer hover:text-emerald-800 w-fit">
          <Upload size={14} />
          {uploadKycDocument.isPending ? "Uploading..." : "Upload / Replace"}
          <input
            type="file"
            accept="image/*,.pdf"
            className="hidden"
            disabled={uploadKycDocument.isPending}
            onChange={handleFileChange}
          />
        </label>
      </div>
    </div>
  );
};

const UserForm = () => {
  const { uuid } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  // Use the useUserById query hook
  const { data: response, isLoading, error, refetch } = useUserById(uuid);
  const userData = response?.user;
  const kycData = response?.kyc;
  const merchantKycVerification = response?.merchantKycVerification;

  // Use the disable user mutation
  const disableUser = useDisableUserById();
  const enableUser = useEnableUserById();
  const deleteUser = useDeleteUserById();
  const restoreUser = useRestoreUserById();
  const requestResetPin = useRequestResetUserPin();
  const confirmResetPin = useConfirmResetUserPin();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isResetPinModalOpen, setIsResetPinModalOpen] = useState(false);
  const retryKyc = useRetryKyc();
  const updateUser = useUpdateUser();
  const approveKyc = useApproveKycById();
  const declineKyc = useUpdateKycById();
  const [isDeclineKycModalOpen, setIsDeclineKycModalOpen] = useState(false);

  const handleRetryKyc = async () => {
    try {
      const result = await retryKyc.mutateAsync({ uuid });
      const status = result?.status;
      const message =
        status === "approved"
          ? "KYC retried - approved"
          : status === "declined"
          ? "KYC retried - still declined"
          : "KYC retried - result is pending";
      toast.success(message);
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to retry KYC");
    }
  };

  const handleApproveKyc = async () => {
    try {
      const promise = approveKyc.mutateAsync({ id: kycData?.id });
      await toast.promise(promise, {
        loading: "Approving KYC...",
        success: "KYC approved successfully",
        error: "Failed to approve KYC",
      });
      refetch();
    } catch (err) {
      console.error("Error approving KYC:", err);
    }
  };

  const handleSubmitDeclineKyc = async (reason) => {
    try {
      const promise = declineKyc.mutateAsync({
        data: { userId: userData?.id, declineReason: reason },
      });
      await toast.promise(promise, {
        loading: "Declining KYC...",
        success: "KYC declined successfully",
        error: "Failed to decline KYC",
      });
      setIsDeclineKycModalOpen(false);
      refetch();
    } catch (err) {
      console.error("Error declining KYC:", err);
    }
  };

  const handleDocumentUploaded = async (fieldKey, uploadResult) => {
    await updateUser.mutateAsync({
      uuid,
      payload: {
        kyc_metadata: {
          ...(kycData?.kyc_metadata || {}),
          [fieldKey]: uploadResult.secure_url,
          [`${fieldKey}_format`]: uploadResult.format,
        },
      },
    });
    refetch();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };
  const handleDecline = () => {
    setIsModalOpen(true);
  };

  const handleSubmitDecline = async () => {
    try {
      const promise = disableUser.mutateAsync(
        uuid,
      );

      await toast.promise(promise, {
        loading: "disabling User...",
        success: "user disabled successfully",
        error: "Failed to enable user",
      });
    } catch (error) {
      console.error("Error enabling user:", error);
    } finally {
      setIsModalOpen(false)
      refetch()
    }
  };

  const handleSubmitDeleteUser = async () => {
    try {
      const promise = deleteUser.mutateAsync(uuid);

      await toast.promise(promise, {
        loading: "Deleting user...",
        success: "User deleted successfully",
        error: "Failed to delete user",
      });
      navigate(-1);
    } catch (error) {
      console.error("Error deleting user:", error);
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  const handleRestoreUser = async () => {
    try {
      const promise = restoreUser.mutateAsync(uuid);

      await toast.promise(promise, {
        loading: "Restoring user...",
        success: "User restored successfully",
        error: "Failed to restore user",
      });
    } catch (error) {
      console.error("Error restoring user:", error);
    } finally {
      refetch();
    }
  };

  const handleEnableUser = async () => {
    try {
      const promise = enableUser.mutateAsync(
        uuid,
      );

      await toast.promise(promise, {
        loading: "Enabling User...",
        success: "user disabled successfully",
        error: "Failed to disable user",
      });
    } catch (error) {
      console.error("Error disabling user:", error);
    } finally {
      refetch()
    }
  };

  if (isLoading) {
    return (
      <div className="flex overflow-hidden flex-col h-screen md:flex-row">
        {/* Sidebar handles its own desktop/mobile rendering */}
        <Sidebar />
        
        {/* Main content — scrolls independently */}
        <div className="flex flex-1 justify-center items-center">
          <p className="text-gray-600">Loading user data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex overflow-hidden flex-col h-screen md:flex-row">
        {/* Sidebar handles its own desktop/mobile rendering */}
        <Sidebar />
        
        {/* Main content — scrolls independently */}
        <div className="flex flex-1 justify-center items-center">
          <p className="text-red-600">Error: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex overflow-hidden flex-col h-screen md:flex-row">
      {/* Sidebar handles its own desktop/mobile rendering */}
      <Sidebar />
      
      {/* Main content — scrolls independently */}
      <div className="overflow-y-auto flex-1 p-4 py-7 mb-9 bg-white md:py-7">
        <div className="px-2 mx-auto md:px-8">
          <div className="flex justify-between items-start mb-8">
            <div className="space-y-1">
              {/* <h1 className="py-2 text-2xl font-semibold">KYC Requests</h1> */}
              <h1 className="py-2 text-xl font-semibold md:text-2xl">Consumer Details</h1>
              <button
                className="flex items-center text-[#013941]"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-4 h-4 mr-1 text-[#013941]" />
                Back
              </button>
            </div>
            <div className="flex items-center mt-3 space-x-3">
              <button
                className="px-2 py-1 font-sans text-emerald-800 rounded-full border border-emerald-800 transition-colors md:px-6 md:py-2 hover:bg-emerald-50"
                onClick={() => navigate(`/users/${uuid}/edit`)}
              >
                Edit
              </button>
              {userData?.deletedAt ? (
                <button
                  className={`px-1 py-1 font-sans text-base font-bold text-white bg-emerald-800 rounded-full transition-colors md:px-6 md:py-2 hover:bg-emerald-900 ${
                    restoreUser.isPending ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  onClick={handleRestoreUser}
                  disabled={restoreUser.isPending}
                >
                  {restoreUser.isPending ? "Restoring..." : "Restore user"}
                </button>
              ) : (
                <>
                  {userData?.isActive ? (
                    <button
                      className={`px-2 py-1 md:px-6 md:py-2 border border-[#EB5757] text-[#EB5757] rounded-full font-sans ${
                        disableUser.isPending ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      onClick={handleDecline}
                      disabled={disableUser.isPending}
                    >
                      {disableUser.isPending ? "Disabling..." : "Disable user"}
                    </button>
                  ) : (
                    <button
                      className="px-1 py-1 font-sans text-base font-bold text-white bg-emerald-800 rounded-full transition-colors md:px-6 md:py-2 hover:bg-emerald-900"
                      onClick={handleEnableUser}
                      disabled={enableUser.isPending}
                    >
                      activate user
                    </button>
                  )}
                  <button
                    className={`px-2 py-1 md:px-6 md:py-2 border border-[#EB5757] text-[#EB5757] rounded-full font-sans ${
                      deleteUser.isPending ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    onClick={() => setIsDeleteModalOpen(true)}
                    disabled={deleteUser.isPending}
                  >
                    {deleteUser.isPending ? "Deleting..." : "Delete user"}
                  </button>
                </>
              )}
              <button
                className="px-2 py-1 md:px-6 md:py-2 border border-[#EB5757] text-[#EB5757] rounded-full font-sans"
                onClick={() => setIsResetPinModalOpen(true)}
              >
                Reset PIN
              </button>
              <DisableUserModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleSubmitDecline}
                isLoading={disableUser.isPending}
              />
              <DisableUserModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onSubmit={handleSubmitDeleteUser}
                isLoading={deleteUser.isPending}
                title="Delete User"
                description="Are you sure you want to permanently delete this user's account? This can be undone by restoring them from the database if needed, but they will lose access immediately and stop receiving all emails."
                confirmLabel="Delete user"
              />
              <ResetPinModal
                isOpen={isResetPinModalOpen}
                onClose={() => setIsResetPinModalOpen(false)}
                isRequesting={requestResetPin.isPending}
                isConfirming={confirmResetPin.isPending}
                onRequestCode={async () => {
                  try {
                    await requestResetPin.mutateAsync(uuid);
                    toast.success("Confirmation code sent to your email");
                  } catch (err) {
                    toast.error(err?.response?.data?.message || "Failed to send code");
                  }
                }}
                onConfirmCode={async (code) => {
                  try {
                    await confirmResetPin.mutateAsync({ uuid, code });
                    toast.success("PIN reset - the user will set a new one");
                  } catch (err) {
                    toast.error(err?.response?.data?.message || "Failed to confirm code");
                  }
                }}
              />
            </div>
          </div>

          {/* Form */}
          <div className="space-y-6 border border-[#CCF6E5] rounded-3xl px-4 py-6 md:px-8 md:py-8">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 text-sm text-gray-600">
                  First name
                </label>
                <input
                  type="text"
                  value={userData?.firstName || ""}
                  className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-base font-medium"
                  readOnly
                />
              </div>
              <div>
                <label className="block mb-1 text-sm text-gray-600">
                  Last name
                </label>
                <input
                  type="text"
                  value={userData?.lastName || ""}
                  className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-base font-medium"
                  readOnly
                />
              </div>
            </div>

            {/* Contact Fields */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm text-gray-600">
                  Email
                </label>
                <input
                  type="email"
                  value={userData?.email || ""}
                  className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-base font-medium"
                  readOnly
                />
              </div>
              <div>
                <label className="block mb-1 text-sm text-gray-600">
                  Phone number
                </label>
                <div className="flex">
                  <select className="p-2 border rounded-l-full border-r-0 bg-gray-50 text-[#000E10] text-sm font-medium outline-none">
                    <option>+234</option>
                  </select>
                  <input
                    type="text"
                    value={userData?.phoneNumber?.replace("+234", "") || ""}
                    className="outline-none font-sans w-full p-2 border rounded-r-full text-[#000E10] text-base font-medium"
                    readOnly
                  />
                </div>
              </div>
            </div>

            {/* Date of Birth */}
            <div className="max-w-xs">
              <label className="block mb-1 text-sm text-gray-600">
                Date of birth
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formatDate(userData?.dob) || ""}
                  className="outline-none w-full p-2 pl-10 border rounded-full text-[#000E10] text-base font-medium"
                  readOnly
                />
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#013941]" />
              </div>
            </div>

            {/* Status Fields */}
            <div className="space-y-2">
              <h3 className="font-medium text-[#828282] text-base py-3">
                Verification Status
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 text-sm text-gray-600">
                    Email Status
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={userData?.isEmailVerified ? "Verified" : "Pending"}
                      className="w-full outline-none p-2 pr-24 border rounded-full font-sans text-[#828282]"
                      readOnly
                    />
                    <button
                      className={`absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 ${
                        userData?.isEmailVerified
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-yellow-100 text-yellow-800"
                      } rounded-lg text-sm`}
                    >
                      {userData?.isEmailVerified ? "Verified" : "Pending"}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">
                    Phone Status
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={userData?.isPhoneVerified ? "Verified" : "Pending"}
                      className="w-full outline-none p-2 pr-24 border rounded-full font-sans text-[#828282]"
                      readOnly
                    />
                    <button
                      className={`absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 ${
                        userData?.isPhoneVerified
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-yellow-100 text-yellow-800"
                      } rounded-lg text-sm`}
                    >
                      {userData?.isPhoneVerified ? "Verified" : "Pending"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Status */}
            <div>
              <label className="block mb-1 text-sm text-gray-600">
                Account Status
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={userData?.isActive ? "Active" : "Inactive"}
                  className="w-full outline-none p-2 pr-24 border rounded-full font-sans text-[#828282]"
                  readOnly
                />
                <button
                  className={`absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 ${
                    userData?.isActive
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-red-100 text-red-800"
                  } rounded-lg text-sm`}
                >
                  {userData?.isActive ? "Active" : "Inactive"}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block mb-1 text-sm text-gray-600">
                    Third Party Payment Status
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={userData?.isThirdPartyPaymentAllowed ? "Allowed" : "Not Allowed"}
                      className="w-full outline-none p-2 pr-24 border rounded-full font-sans text-[#828282]"
                      readOnly
                    />
                    <button
                      className={`absolute right-2 top-1/2 -translate-y-1/2 px-1 py-1 ${
                        userData?.isThirdPartyPaymentAllowed
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-red-100 text-red-800"
                      } rounded-lg text-sm`}
                    >
                      {userData?.isThirdPartyPaymentAllowed ? "Allowed" : "Not Allowed"}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block mb-1 text-sm text-gray-600">
                    Is Ambassador ?
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={userData?.is_ambassador ? "Yes" : "No"}
                      className="w-full outline-none p-2 pr-24 border rounded-full font-sans text-[#828282]"
                      readOnly
                    />
                    <button
                      className={`absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 ${
                        userData?.is_ambassador
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-red-100 text-red-800"
                      } rounded-lg text-sm`}
                    >
                      {userData?.is_ambassador ? "Yes" : "No"}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block mb-1 text-sm text-gray-600">
                    Is OTC Customer ?
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={userData?.isOtcCustomer ? "Yes" : "No"}
                      className="w-full outline-none p-2 pr-24 border rounded-full font-sans text-[#828282]"
                      readOnly
                    />
                    <button
                      className={`absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 ${
                        userData?.isOtcCustomer
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-red-100 text-red-800"
                      } rounded-lg text-sm`}
                    >
                      {userData?.isOtcCustomer ? "Yes" : "No"}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block mb-1 text-sm text-gray-600">
                    Is High Risk ?
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={userData?.isHighRisk ? "Yes" : "No"}
                      className="w-full outline-none p-2 pr-24 border rounded-full font-sans text-[#828282]"
                      readOnly
                    />
                    <button
                      className={`absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 ${
                        userData?.isHighRisk
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-red-100 text-red-800"
                      } rounded-lg text-sm`}
                    >
                      {userData?.isHighRisk ? "Yes" : "No"}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block mb-1 text-sm text-gray-600">
                    OTC Platform Percentage (%)
                  </label>
                  <input
                    type="text"
                    value={userData?.otcPlatformPercentage || ""}
                    className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-base font-medium"
                    readOnly
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm text-gray-600">
                    Reserve Percentage (%)
                  </label>
                  <input
                    type="text"
                    value={userData?.reservePercentage || ""}
                    className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-base font-medium"
                    readOnly
                  />
                </div>
              </div>
            </div>

            {/* Identification */}
            <div className="space-y-2">
              <h3 className="font-medium text-[#828282] text-base py-3">
                Identification
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-sm text-gray-600">
                      Means of identification
                    </label>
                    {/* {!userData?.identification?.type && (
                      <button className="flex items-center gap-2 text-[#013941]">
                        <span className="mr-5 text-sm font-semibold">
                          View document
                        </span>
                      </button>
                    )} */}
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      value={userData?.identification?.type || "Not provided"}
                      className="w-full outline-none p-2 pr-24 border rounded-full font-sans text-[#828282]"
                      readOnly
                    />
                    <button className="absolute right-2 top-1/2 px-3 py-1 text-sm text-yellow-800 bg-yellow-100 rounded-lg -translate-y-1/2">
                      {userData?.identification?.verified
                        ? "Verified"
                        : "Pending"}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">
                    NIN
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={userData?.identification?.nin || "Not provided"}
                      className="w-full outline-none p-2 pr-24 border rounded-full text-[#000E10] text-sm font-medium py-3"
                      readOnly
                    />
                    <button className="absolute right-2 top-1/2 px-3 py-1 text-sm text-yellow-800 bg-yellow-100 rounded-lg -translate-y-1/2">
                      {userData?.identification?.ninVerified
                        ? "Verified"
                        : "Pending"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <h3 className="font-medium text-[#828282] text-base py-3">
                Address
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 text-sm text-gray-600">
                    Street address
                  </label>
                  <input
                    type="text"
                    value={kycData?.address || "Not provided"}
                    className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-base font-medium"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">
                    Town/City
                  </label>
                  <input
                    type="text"
                    value={kycData?.city || "Not provided"}
                    className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-base font-medium"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">
                    State
                  </label>
                  <input
                    type="text"
                    value={kycData?.state || "Not provided"}
                    className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-base font-medium"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">
                    Country
                  </label>
                  <input
                    type="text"
                    value={userData?.country || "Not provided"}
                    className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-base font-medium capitalize"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">
                    Postal code
                  </label>
                  <input
                    type="text"
                    value={kycData?.postalCode || "Not provided"}
                    className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-base font-medium py-2"
                    readOnly
                  />
                </div>
              </div>
            </div>

            {/* kyc */}
            <div className="space-y-2">
              <h3 className="font-medium text-[#828282] text-base py-3">
                KYC
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 text-sm text-gray-600">
                    ID Type
                  </label>
                  <input
                    type="text"
                    value={kycData?.kyc_metadata?.id_type || "Not provided"}
                    className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-base font-medium"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-600">
                    ID Number
                  </label>
                  <input
                    type="text"
                    value={kycData?.kyc_metadata?.id_number || "Not provided"}
                    className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-base font-medium"
                    readOnly
                  />
                </div>

                 <div>
                  <label className="block mb-1 text-sm text-gray-600">
                    KYC Status
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={kycData?.status || "Not provided"}
                      className="w-full outline-none p-2 pr-24 border rounded-full text-[#000E10] text-base font-medium py-3 capitalize"
                      readOnly
                    />
                    <button className={`absolute right-2 top-1/2 px-3 py-1 text-sm rounded-lg -translate-y-1/2 ${kycData?.status === "approved" ? "text-green-800 bg-green-200" : kycData?.status === "declined" ? "text-red-800 bg-red-100" : "text-yellow-800 bg-yellow-100"}`}>
                      {kycData?.status === "approved" ? "Approved" : kycData?.status === "declined" ? "Declined" : "Pending"}
                    </button>
                  </div>
                </div>

                {kycData?.status && (
                  <div className="flex flex-wrap gap-3 items-end">
                    {kycData.status !== "approved" && (
                      <button
                        onClick={handleApproveKyc}
                        disabled={approveKyc.isPending}
                        className="px-4 py-2 text-sm font-medium text-white bg-emerald-800 rounded-full hover:bg-emerald-900 disabled:opacity-50"
                      >
                        {approveKyc.isPending ? "Approving..." : "Approve KYC"}
                      </button>
                    )}
                    {kycData.status !== "declined" && (
                      <button
                        onClick={() => setIsDeclineKycModalOpen(true)}
                        className="px-4 py-2 text-sm font-medium text-[#EB5757] rounded-full border border-[#EB5757] hover:bg-red-50"
                      >
                        Decline KYC
                      </button>
                    )}
                    {kycData.status !== "approved" && (
                      <button
                        onClick={handleRetryKyc}
                        disabled={retryKyc.isPending}
                        className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-emerald-700 rounded-full border-2 border-emerald-500 hover:bg-emerald-50 disabled:opacity-50"
                      >
                        <RotateCcw size={14} />
                        {retryKyc.isPending ? "Retrying..." : "Retry KYC"}
                      </button>
                    )}
                    <DeclineKYCModal
                      isOpen={isDeclineKycModalOpen}
                      onClose={() => setIsDeclineKycModalOpen(false)}
                      onSubmit={handleSubmitDeclineKyc}
                    />
                  </div>
                )}

                {/* Admin-only debugging: verification provider references, not shown to the customer */}
                <div className="col-span-2 p-4 space-y-2 bg-gray-50 rounded-2xl border border-gray-200">
                  <h4 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                    Verification Provider IDs (Admin Debugging)
                  </h4>
                  <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                    <div>
                      <span className="text-gray-500">Document/BVN Verification Reference (consumer KYC record): </span>
                      <span className="font-mono text-[#000E10]">
                        {kycData?.kycProviderVerificationReference || "Not available"}
                        {kycData?.kycProviderName ? ` (${kycData.kycProviderName})` : ""}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Document/BVN Verification Reference (merchant record): </span>
                      <span className="font-mono text-[#000E10]">
                        {merchantKycVerification?.reference || "Not available"}
                        {merchantKycVerification?.providerName ? ` (${merchantKycVerification.providerName})` : ""}
                      </span>
                      {kycData?.kycProviderVerificationReference &&
                        merchantKycVerification?.reference &&
                        kycData.kycProviderVerificationReference !== merchantKycVerification.reference && (
                          <p className="mt-1 text-xs text-amber-600">
                            ⚠ Doesn&apos;t match the consumer record above
                          </p>
                        )}
                    </div>
                    <div>
                      <span className="text-gray-500">Locally-Saved Provider Status: </span>
                      <span className="text-[#000E10]">
                        {kycData?.kycProviderVerificationStatus || merchantKycVerification?.verificationStatus || "Not available"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Provider Already Charged: </span>
                      <span className="text-[#000E10]">
                        {(kycData?.kycProviderWasCharged ?? merchantKycVerification?.wasCharged) === true
                          ? "Yes"
                          : (kycData?.kycProviderWasCharged ?? merchantKycVerification?.wasCharged) === false
                          ? "No"
                          : "Unknown"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Pro KYC (Liveness) Session ID: </span>
                      <span className="font-mono text-[#000E10]">
                        {kycData?.livenessProviderSessionId || "Not available"}
                      </span>
                    </div>
                    {kycData?.livenessSessionStatus && (
                      <div>
                        <span className="text-gray-500">Liveness Session Status: </span>
                        <span className="text-[#000E10]">{kycData.livenessSessionStatus}</span>
                      </div>
                    )}
                    {kycData?.livenessFailureReason && (
                      <div>
                        <span className="text-gray-500">Liveness Failure Reason: </span>
                        <span className="text-[#000E10]">{kycData.livenessFailureReason}</span>
                      </div>
                    )}
                    {kycData?.providerDeclineReason && (
                      <div className="sm:col-span-2">
                        <span className="text-gray-500">Provider Decline Reason: </span>
                        <span className="text-[#000E10]">{kycData.providerDeclineReason}</span>
                      </div>
                    )}
                  </div>
                </div>

                {(() => {
                  const metadata = kycData?.kyc_metadata || {};
                  const imageEntries = Object.entries(metadata).filter(
                    ([key, value]) =>
                      !key.endsWith("_format") &&
                      typeof value === "string" &&
                      value.startsWith("http")
                  );

                  if (imageEntries.length > 0) {
                    return imageEntries.map(([key, value]) => (
                      <KycDocumentPreview
                        key={key}
                        label={key.replace(/_/g, " ")}
                        url={value}
                        format={metadata[`${key}_format`]}
                        uuid={uuid}
                        fieldKey={key}
                        onUploaded={handleDocumentUploaded}
                      />
                    ));
                  }

                  return ["id_image", "selfie_image"].map((key) => (
                    <KycDocumentPreview
                      key={key}
                      label={key.replace(/_/g, " ")}
                      url={null}
                      format={null}
                      uuid={uuid}
                      fieldKey={key}
                      onUploaded={handleDocumentUploaded}
                    />
                  ));
                })()}
      
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserForm;
